import React from 'react';
import { motion } from 'framer-motion';
import Users from 'lucide-react/dist/esm/icons/users';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up';
import Award from 'lucide-react/dist/esm/icons/award';
import Heart from 'lucide-react/dist/esm/icons/heart';
import Mail from 'lucide-react/dist/esm/icons/mail';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import Lightbulb from 'lucide-react/dist/esm/icons/lightbulb';
import Target from 'lucide-react/dist/esm/icons/target';

const CareersPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Professional Growth',
      description: 'Continuous learning opportunities and career advancement in a dynamic environment',
    },
    {
      icon: Users,
      title: 'Collaborative Culture',
      description: 'Work alongside experienced professionals from top-tier firms like PwC, KPMG, and EY',
    },
    {
      icon: Award,
      title: 'Industry Recognition',
      description: 'Be part of a SAICA-accredited firm with strong professional credentials',
    },
    {
      icon: Heart,
      title: 'Meaningful Impact',
      description: "Contribute to South Africa's economic transformation and public sector excellence",
    },
  ];

  const values = [
    {
      icon: Award,
      title: 'Excellence',
      description: 'We maintain the highest professional standards in all our engagements',
    },
    {
      icon: Heart,
      title: 'Integrity',
      description: 'Transparency and ethical conduct guide every decision we make',
    },
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'We continuously evolve our services to meet changing business needs',
    },
    {
      icon: Target,
      title: 'Empowerment',
      description: 'We contribute to transformation through skills development and knowledge transfer',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-secondary py-20">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div className="text-white" {...fadeInUp}>
              <h1 className="text-4xl md:text-5xl lg:text-5xl font-bold leading-snug md:leading-normal lg:leading-[1.25] mb-6">
                <span className="text-cyan-400">Build Your Career</span> with OMC
              </h1>
              <p className="text-xl md:text-xl text-blue-100 max-w-4xl mx-auto mb-10">
                Join a leading chartered accounting firm where excellence meets opportunity. Grow your career alongside industry experts and make a meaningful impact in South Africa's financial landscape.
              </p>
              <a
                href="mailto:admin@omcca.co.za?subject=Career Inquiry"
                className="bg-white text-primary px-8 py-4 rounded-lg font-semibold hover:bg-gray-200 transition-colors inline-flex items-center  bg-transparent focus:outline-none"
              >
                <Mail className="mr-2 h-5 w-5" />
                Send Your CV
              </a>
            </motion.div>
            <motion.div
  initial={{ opacity: 0, x: 60 }}
  animate={{ opacity: 1, x: 0 }}
  transition={{ duration: 0.8, delay: 0.2 }}
  className="relative"
>
  <img
    src="https://media.phst.co.za/file/omc/omc-careers.jpg?w=600&h=400&fit=crop"
    alt="Professional office environment"
    className="rounded-2xl shadow-xl w-full"
  />
  <div className="absolute inset-0 bg-[#0164CD] opacity-10 rounded-2xl"></div>
</motion.div>
          </div>
        </div>
      </section>

      {/* Why Join OMC */}
      <section className="py-20">
        <div className="container-custom">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose OMC for Your Career?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              At OMC Chartered Accountants, we believe in nurturing talent and providing opportunities for professional growth in a supportive, values-driven environment.
            </p>
          </motion.div>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="bg-background p-8 rounded-xl hover:shadow-lg transition-shadow"
                variants={fadeInUp}
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-primary text-white rounded-lg">
                      <benefit.icon className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="ml-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Our Culture */}
      <section className="py-20  bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Culture & Values</h2>
              <div className="space-y-6 text-lg text-gray-600">
                <p>
                  OMC Chartered Accountants is more than just a workplace – it's a community of professionals committed to excellence, integrity, and making a positive impact. Our culture is built on mutual respect, continuous learning, and the shared goal of delivering exceptional service to our clients.
                </p>
                <p>
                  As a proudly black-owned and black-managed firm, we are deeply committed to transformation and empowerment in the South African professional services sector. We believe in creating opportunities for growth and development while maintaining the highest professional standards.
                </p>
                <p>
                  Our team members benefit from exposure to diverse clients across both public and private sectors, gaining invaluable experience that accelerates career growth and professional development.
                </p>
              </div>
            </motion.div>
            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 gap-6"
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {values.map((value, index) => (
                <div key={index} className="bg-white p-6 rounded-xl text-center shadow-sm">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-primary text-white rounded-lg mb-4">
                    <value.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-gray-600 text-sm">{value.description}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-20">
        <div className="container-custom">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Career Opportunities
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We are always looking for talented professionals to join our growing team across various disciplines and levels.
            </p>
          </motion.div>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {[
              {
                title: 'Audit & Assurance',
                positions: ['Audit Trainees', 'Junior Auditors', 'Senior Auditors', 'Audit Managers'],
              },
              {
                title: 'Internal Audit',
                positions: [
                  'Internal Audit Trainees',
                  'Internal Auditors',
                  'Senior Internal Auditors',
                  'Internal Audit Managers',
                ],
              },
              {
                title: 'Consulting Services',
                positions: [
                  'Junior Consultants',
                  'Senior Consultants',
                  'Consulting Managers',
                  'Principal Consultants',
                ],
              },
              {
                title: 'Forensic Investigations',
                positions: [
                  'Forensic Analysts',
                  'Senior Forensic Investigators',
                  'Forensic Managers',
                ],
              },
              {
                title: 'IT Risk & Advisory',
                positions: [
                  'IT Risk Analysts',
                  'IT Auditors',
                  'IT Risk Managers',
                  'IT Advisory Consultants',
                ],
              },
              {
                title: 'Administration & Support',
                positions: [
                  'Administrative Assistants',
                  'Office Managers',
                  'HR Coordinators',
                  'Finance Officers',
                ],
              },
            ].map((department, index) => (
              <motion.div
                key={index}
                className="bg-background p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                variants={fadeInUp}
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{department.title}</h3>
                <ul className="space-y-2">
                  {department.positions.map((position, posIndex) => (
                    <li key={posIndex} className="text-gray-600 text-sm flex items-center">
                      <div className="w-2 h-2 bg-primary rounded-full mr-3 flex-shrink-0"></div>
                      {position}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-20 bg-background">
        <div className="container-custom">
          <div className="w-full max-w-5xl mx-auto">
            <motion.div className="text-center" {...fadeInUp}>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">How to Apply</h2>
              <p className="text-xl text-gray-600 mb-12">
                Ready to take the next step in your career? Here's how to join our team.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                <div className="bg-white p-8 rounded-xl shadow-md">
                  <div className="flex items-center justify-center w-16 h-16 bg-primary text-white rounded-full mb-4 mx-auto">
                    <FileText className="h-8 w-8" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    1. Prepare Your Application
                  </h3>
                  <p className="text-gray-600">
                    Update your CV and prepare a compelling cover letter highlighting your relevant experience and qualifications.
                  </p>
                </div>

                <div className="bg-white p-8 rounded-xl shadow-md">
                  <div className="flex items-center justify-center w-16 h-16 bg-secondary text-white rounded-full mb-4 mx-auto">
                    <Mail className="h-8 w-8" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    2. Submit Your Application
                  </h3>
                  <p className="text-gray-600">
                    Send your CV and cover letter to our HR team via email with a subject line indicating your area of interest.
                  </p>
                </div>

                <div className="bg-white p-8 rounded-xl shadow-md">
                  <div className="flex items-center justify-center w-16 h-16 bg-accent text-white rounded-full mb-4 mx-auto">
                    <Users className="h-8 w-8" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    3. Interview Process
                  </h3>
                  <p className="text-gray-600">
                    Successful candidates will be contacted for an interview process designed to assess fit and potential.
                  </p>
                </div>
              </div>
              <div className="text-center">
                <a
                  href="mailto:admin@omcca.co.za?subject=Career Application - [Your Area of Interest]"
                  className="bg-[#0383ED] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#1A95FF] transition-colors inline-flex items-center"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Submit Your Application
                </a>
                <p className="text-gray-500 text-sm mt-4">
                  Email: admin@omcca.co.za | Phone: +27 10 001 1234
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;